// Region context — persists the currently-selected region in localStorage.
//
// In production each region has a SEPARATE admin host
// (automationai-admin[-dv|-eu].cloudradial.com). Switching region in the
// header normally means "navigate to the other regional host and re-auth"
// because each region's PlatformAdminUser row is independent (§3a).
//
// In dev there's only one backend; switching the picker is a no-op for
// network calls — the AppShell still re-routes through /login so the
// admin re-authenticates against the (logically) different region.

import { createContext, useContext } from 'react';
import type { RegionCode } from '@shared/api-types';

const STORAGE_KEY = 'crauto-admin-region';
const DEFAULT_REGION: RegionCode = 'dv';

export interface RegionContextValue {
  region: RegionCode;
  setRegion: (region: RegionCode) => void;
}

export const RegionContext = createContext<RegionContextValue | null>(null);

export function useRegion(): RegionContextValue {
  const ctx = useContext(RegionContext);
  if (!ctx) {
    throw new Error('useRegion() must be used inside a <RegionProvider>.');
  }
  return ctx;
}

export function readPersistedRegion(): RegionCode {
  if (typeof window === 'undefined') {
    return DEFAULT_REGION;
  }
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (raw === 'dv' || raw === 'us' || raw === 'eu') {
    return raw;
  }
  return DEFAULT_REGION;
}

export function persistRegion(region: RegionCode): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(STORAGE_KEY, region);
}

export const REGION_STORAGE_KEY = STORAGE_KEY;
